import { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Avatar,
  Menu,
  MenuItem,
  useTheme,
  useMediaQuery,
  Button,
  Badge,
  Tooltip,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard,
  People,
  Business,
  Assignment,
  Logout,
  ChevronLeft,
  ChevronRight,
  Grass,
  Tune,
  ManageAccounts,
  Payment,
  Key,
  History,
  Label,
  Extension,
  Notifications as NotificationsIcon,
  Settings,
  Send,
  Insights,
  Campaign,
  Hub,
} from '@mui/icons-material';
import { useAuthStore, useChannelStore } from '../../store';
import { HeaderChannelSelector } from '../common';
import { usePendingApprovalCount } from '../../hooks/useBranchPlans';
import { useContractorYandexAnalyticsPath } from '../../hooks/useYandex';
import { usePaymentIndicators } from '../../hooks/usePayments';
import { useUnreadCount } from '../../hooks/useNotifications';
import type { Role } from '@ads-manager/shared';

const drawerWidth = 240;
const collapsedDrawerWidth = 64;
const SIDEBAR_COLLAPSED_KEY = 'sidebarCollapsed';

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  roles: Role[];
}

const navItems: NavItem[] = [
  {
    label: 'Дашборд',
    path: '/tm/dashboard',
    icon: <Dashboard />,
    roles: ['TRAFFIC_MANAGER'],
  },
  {
    label: 'Аналитика',
    path: '/tm/analytics',
    icon: <Insights />,
    roles: ['TRAFFIC_MANAGER'],
  },
  {
    label: 'Яндекс Директ',
    path: '/tm/yandex-analytics',
    icon: <Campaign />,
    roles: ['TRAFFIC_MANAGER'],
  },
  {
    label: 'Все источники',
    path: '/tm/sources-analytics',
    icon: <Hub />,
    roles: ['TRAFFIC_MANAGER'],
  },
  {
    label: 'Подрядчики',
    path: '/tm/contractors',
    icon: <People />,
    roles: ['TRAFFIC_MANAGER'],
  },
  {
    label: 'Филиалы',
    path: '/tm/branches',
    icon: <Business />,
    roles: ['TRAFFIC_MANAGER'],
  },
  {
    label: 'Планы',
    path: '/tm/plans',
    icon: <Assignment />,
    roles: ['TRAFFIC_MANAGER'],
  },
  {
    label: 'Органика',
    path: '/tm/organic-sources',
    icon: <Grass />,
    roles: ['TRAFFIC_MANAGER'],
  },
  {
    label: 'Каналы',
    path: '/tm/traffic-channels',
    icon: <Tune />,
    roles: ['TRAFFIC_MANAGER'],
  },
  {
    label: 'Менеджеры',
    path: '/tm/managers',
    icon: <ManageAccounts />,
    roles: ['TRAFFIC_MANAGER'],
  },
  {
    label: 'Платежи',
    path: '/tm/payments',
    icon: <Payment />,
    roles: ['TRAFFIC_MANAGER'],
  },
  {
    label: 'Рассылка',
    path: '/tm/mailings',
    icon: <Send />,
    roles: ['TRAFFIC_MANAGER'],
  },
  {
    label: 'API',
    path: '/tm/api-clients',
    icon: <Key />,
    roles: ['TRAFFIC_MANAGER'],
  },
  {
    label: 'История API',
    path: '/tm/api-call-logs',
    icon: <History />,
    roles: ['TRAFFIC_MANAGER'],
  },
  {
    label: 'Атрибуты заявок',
    path: '/tm/lead-attributes',
    icon: <Label />,
    roles: ['TRAFFIC_MANAGER'],
  },
  {
    label: 'Интеграции',
    path: '/tm/integrations',
    icon: <Extension />,
    roles: ['TRAFFIC_MANAGER'],
  },
  {
    label: 'Дашборд',
    path: '/contractor/dashboard',
    icon: <Dashboard />,
    roles: ['CONTRACTOR'],
  },
  {
    label: 'Платежи',
    path: '/contractor/payments',
    icon: <Payment />,
    roles: ['CONTRACTOR'],
  },
  {
    label: 'Дашборд',
    path: '/branch/dashboard',
    icon: <Dashboard />,
    roles: ['BRANCH'],
  },
  {
    label: 'Планы',
    path: '/branch/plans',
    icon: <Assignment />,
    roles: ['BRANCH'],
  },
  {
    label: 'Платежи',
    path: '/branch/payments',
    icon: <Payment />,
    roles: ['BRANCH'],
  },
  // Account Manager navigation
  {
    label: 'Филиалы',
    path: '/am/dashboard',
    icon: <Business />,
    roles: ['ACCOUNT_MANAGER'],
  },
];

const getRoleLabel = (role: Role): string => {
  switch (role) {
    case 'TRAFFIC_MANAGER':
      return 'Трафик-менеджер';
    case 'ACCOUNT_MANAGER':
      return 'Аккаунт-менеджер';
    case 'CONTRACTOR':
      return 'Подрядчик';
    case 'BRANCH':
      return 'Филиал';
    default:
      return role;
  }
};

const getAccountSettingsPath = (role: Role): string => {
  switch (role) {
    case 'TRAFFIC_MANAGER':
      return '/tm/settings';
    case 'CONTRACTOR':
      return '/contractor/settings';
    case 'BRANCH':
      return '/branch/settings';
    case 'ACCOUNT_MANAGER':
      return '/am/settings';
  }
};

const readCollapsedPreference = (): boolean => {
  try {
    return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true';
  } catch {
    return false;
  }
};

export const MainLayout = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, hasRole } = useAuthStore();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(readCollapsedPreference);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  // Получаем количество планов на согласование (только для BRANCH)
  const isBranch = hasRole(['BRANCH']);
  const { data: pendingCountData } = usePendingApprovalCount(isBranch);
  const pendingCount = pendingCountData?.count ?? 0;

  // Получаем индикаторы платежей (для CONTRACTOR и BRANCH)
  const isContractor = hasRole(['CONTRACTOR']);
  const { data: paymentIndicators } = usePaymentIndicators(isContractor || isBranch);
  const showPaymentIndicator = paymentIndicators?.showIndicator ?? false;

  // Инбокс (колокол): для ТМ и подрядчика
  const isInboxRole = hasRole(['TRAFFIC_MANAGER', 'CONTRACTOR']);
  const { data: unreadData } = useUnreadCount(isInboxRole);
  const unreadCount = unreadData?.count ?? 0;
  const inboxPath =
    user?.role === 'CONTRACTOR' ? '/contractor/inbox' : '/tm/inbox';

  // У трафик-менеджера пунктов слишком много для шапки — меню уезжает вбок
  const isTrafficManager = hasRole(['TRAFFIC_MANAGER']);
  const showSideNav = isTrafficManager && !isMobile;
  const sideNavWidth = sidebarCollapsed ? collapsedDrawerWidth : drawerWidth;

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleSidebarToggle = () => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(next));
      } catch {
        // localStorage может быть недоступен — состояние живёт только в памяти
      }
      return next;
    });
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleMenuClose();
    logout();
    navigate('/login');
  };

  const handleAccountSettings = () => {
    if (!user) return;
    handleMenuClose();
    navigate(getAccountSettingsPath(user.role));
  };

  const handleNavClick = (path: string) => {
    navigate(path);
    if (isMobile) {
      setDrawerOpen(false);
    }
  };

  // Сквозная аналитика Яндекса у подрядчика — только в канале, где есть
  // подключения с данными; иначе путь к ней лежал через настройки аккаунта.
  const selectedChannelId = useChannelStore((state) => state.selectedChannel?.id);
  const yandexAnalyticsPath = useContractorYandexAnalyticsPath(
    selectedChannelId,
    isContractor
  );

  const filteredNavItems = navItems.filter((item) => hasRole(item.roles));
  if (yandexAnalyticsPath) {
    filteredNavItems.push({
      label: 'Яндекс Директ',
      path: yandexAnalyticsPath,
      icon: <Campaign />,
      roles: ['CONTRACTOR'],
    });
  }

  const isActivePath = (path: string) => location.pathname === path;

  const getBadgeState = (path: string) => {
    const showPlansBadge = path === '/branch/plans' && pendingCount > 0;
    const showPaymentsBadge =
      (path === '/contractor/payments' || path === '/branch/payments') &&
      showPaymentIndicator;
    return {
      showBadge: showPlansBadge || showPaymentsBadge,
      badgeContent: showPlansBadge ? pendingCount : undefined,
      dot: showPaymentsBadge,
    };
  };

  const renderNavIcon = (item: NavItem) => {
    const { showBadge, badgeContent, dot } = getBadgeState(item.path);
    if (!showBadge) return item.icon;
    return (
      <Badge
        badgeContent={badgeContent}
        color="error"
        variant={dot ? 'dot' : 'standard'}
      >
        {item.icon}
      </Badge>
    );
  };

  // Список пунктов меню — общий для мобильного и бокового меню
  const renderNavList = (collapsed: boolean) => (
    <List sx={{ flexGrow: 1, overflowY: 'auto' }}>
      {filteredNavItems.map((item) => (
        <ListItem key={item.path} disablePadding sx={{ display: 'block' }}>
          <Tooltip
            title={collapsed ? item.label : ''}
            placement="right"
            disableHoverListener={!collapsed}
          >
            <ListItemButton
              onClick={() => handleNavClick(item.path)}
              selected={isActivePath(item.path)}
              sx={{
                minHeight: 44,
                justifyContent: collapsed ? 'center' : 'flex-start',
                px: 2.5,
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: collapsed ? 0 : 2,
                  justifyContent: 'center',
                }}
              >
                {renderNavIcon(item)}
              </ListItemIcon>
              {!collapsed && (
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{ noWrap: true, fontSize: '0.9rem' }}
                />
              )}
            </ListItemButton>
          </Tooltip>
        </ListItem>
      ))}
    </List>
  );

  // Мобильный Drawer
  const mobileDrawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Toolbar
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
        }}
      >
        <Typography variant="h6" noWrap>
          Ads Manager
        </Typography>
        <IconButton onClick={handleDrawerToggle}>
          <ChevronLeft />
        </IconButton>
      </Toolbar>
      <Divider />
      {renderNavList(false)}
      <Divider />
      <Box sx={{ p: 2 }}>
        <Typography variant="caption" color="text.secondary">
          {user?.name}
        </Typography>
        <Typography variant="caption" display="block" color="text.secondary">
          {user && getRoleLabel(user.role)}
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <AppBar position="fixed" sx={{ zIndex: theme.zIndex.drawer + 1 }}>
        <Toolbar>
          {/* Мобильное меню - hamburger */}
          {isMobile && (
            <IconButton
              color="inherit"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          )}

          {/* Логотип */}
          <Typography
            variant="h6"
            noWrap
            component="div"
            sx={{ fontWeight: 'bold', mr: 4 }}
          >
            Ads Manager
          </Typography>

          {/* Десктопная навигация в шапке — для ролей с коротким меню */}
          {!isMobile && !showSideNav && (
            <Box sx={{ display: 'flex', gap: 1, flexGrow: 1 }}>
              {filteredNavItems.map((item) => (
                <Button
                  key={item.path}
                  color="inherit"
                  startIcon={renderNavIcon(item)}
                  onClick={() => handleNavClick(item.path)}
                  sx={{
                    textTransform: 'none',
                    backgroundColor: isActivePath(item.path)
                      ? 'rgba(255, 255, 255, 0.15)'
                      : 'transparent',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.25)',
                    },
                  }}
                >
                  {item.label}
                </Button>
              ))}
            </Box>
          )}

          {/* Spacer, когда навигация не в шапке */}
          {(isMobile || showSideNav) && <Box sx={{ flexGrow: 1 }} />}

          {/* Колокол инбокса (ТМ и подрядчик) */}
          {isInboxRole && (
            <IconButton
              color="inherit"
              onClick={() => navigate(inboxPath)}
              title="Инбокс"
              sx={{ mr: 1 }}
            >
              <Badge badgeContent={unreadCount} color="error" max={99}>
                <NotificationsIcon />
              </Badge>
            </IconButton>
          )}

          {/* Имя, должность и аватар пользователя */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
            }}
            onClick={handleMenuOpen}
          >
            {!isMobile && user && (
              <Box sx={{ mr: 1.5, textAlign: 'right' }}>
                <Typography
                  variant="body2"
                  sx={{ lineHeight: 1.2, fontWeight: 500 }}
                >
                  {user.name}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ lineHeight: 1.2, opacity: 0.8 }}
                >
                  {getRoleLabel(user.role)}
                </Typography>
              </Box>
            )}
            <IconButton color="inherit" sx={{ p: 0 }}>
              <Avatar sx={{ width: 32, height: 32 }}>
                {user?.name?.charAt(0).toUpperCase()}
              </Avatar>
            </IconButton>
          </Box>

          {/* Channel Selector для подрядчиков (справа от аватарки) */}
          {hasRole(['CONTRACTOR']) && <HeaderChannelSelector />}

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
          >
            <MenuItem disabled>
              <Typography variant="body2">{user?.email}</Typography>
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleAccountSettings}>
              <ListItemIcon>
                <Settings fontSize="small" />
              </ListItemIcon>
              Настройки аккаунта
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <Logout fontSize="small" />
              </ListItemIcon>
              Выйти
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Боковое меню трафик-менеджера */}
      {showSideNav && (
        <Drawer
          variant="permanent"
          sx={{
            width: sideNavWidth,
            flexShrink: 0,
            whiteSpace: 'nowrap',
            transition: theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
            '& .MuiDrawer-paper': {
              width: sideNavWidth,
              boxSizing: 'border-box',
              overflowX: 'hidden',
              transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
            },
          }}
        >
          <Toolbar />
          <Box
            sx={{
              height: 'calc(100% - 64px)',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {renderNavList(sidebarCollapsed)}
            <Divider />
            <Box
              sx={{
                display: 'flex',
                justifyContent: sidebarCollapsed ? 'center' : 'flex-end',
                p: 1,
              }}
            >
              <Tooltip
                title={sidebarCollapsed ? 'Развернуть меню' : 'Свернуть меню'}
                placement="right"
              >
                <IconButton onClick={handleSidebarToggle} size="small">
                  {sidebarCollapsed ? <ChevronRight /> : <ChevronLeft />}
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Drawer>
      )}

      {/* Мобильный Drawer */}
      {isMobile && (
        <Drawer
          variant="temporary"
          open={drawerOpen}
          onClose={handleDrawerToggle}
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
            },
          }}
        >
          {mobileDrawer}
        </Drawer>
      )}

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: showSideNav ? `calc(100% - ${sideNavWidth}px)` : '100%',
          mt: '64px',
          backgroundColor: 'background.default',
          minHeight: 'calc(100vh - 64px)',
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
};

export default MainLayout;
